import * as React from "react";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DockPosition } from "./shared/DockPosition";
import useStyles from "./DockedDebugPanel.styles";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { Panel } from "./panels/Panel";
import { StoreState } from "../../models/states/StoreState";
import { useThunkDispatch } from "../../redux/thunks";
import {
    mobilePreviewToggle,
    navigationNextPage,
    navigationPreviousPage,
} from "../../redux/actions";
import useReservedBounds from "../../hooks/useReservedBounds";

interface Props {
    title: string;
    panels: Panel[];
    selected: string;
    position: DockPosition;
    onSelectedChanged(selected: string): void;
}

export default function DockedDebugPanel({
    title,
    panels,
    position,
    selected,
    onSelectedChanged,
}: Props) {
    const classes = useStyles({ selected, position });
    const dispatch = useThunkDispatch();
    const mobilePreview = useSelector((state: StoreState) => state.mobilePreview);
    const { t } = useTranslation();

    useReservedBounds(
        {
            left: position === "left" ? 384 : 0,
            right: position === "right" ? 384 : 0,
        },
        [position],
    );

    return (
        <div className={classes.dockedContainer}>
            <div className={classes.titleRow}>
                <div className={classes.debugIcon}>
                    <FontAwesomeIcon size="lg" icon={["fas", "vial"]} transform="shrink-5" />
                </div>
                <div className={classes.titleContainer}>
                    <h2 className={classes.title}>{t("debug.title")}</h2>
                    <h3 className={classes.subtitle}>{title}</h3>
                </div>
            </div>
            <div className={classes.panelButtonContainer}>
                {panels
                    .filter(({ exclude }) => !exclude)
                    .map(({ onClick, id, icon, active, title }) => (
                        <div
                            key={id}
                            data-tip={title}
                            onClick={onClick || (() => onSelectedChanged(id))}
                            className={classNames(
                                classes.panelButton,
                                active && classes.panelButtonActive,
                            )}
                        >
                            <div className={classes.panelButtonIcon}>{icon}</div>
                        </div>
                    ))}
            </div>
            {panels
                .filter(({ id }) => id === selected)
                .map(({ id, content, index, exclude }) => (
                    <div key={id} className={classes.panel}>
                        {content}
                    </div>
                ))}
            <div className={classes.toolbar}>
                <div
                    onClick={() => dispatch(mobilePreviewToggle())}
                    className={classNames(
                        classes.panelButton,
                        mobilePreview && classes.panelButtonActive,
                    )}
                    data-tip={t("debug.toggleMobilePreview")}
                >
                    <div className={classes.panelButtonIcon}>
                        <FontAwesomeIcon icon={["far", "mobile-android"]} />
                    </div>
                </div>
                <div
                    className={classNames(classes.panelButton, classes.panelButtonSmall)}
                    onClick={() => dispatch(navigationPreviousPage())}
                    data-tip={t("common.previousPage")}
                >
                    <div className={classes.panelButtonIcon}>
                        <FontAwesomeIcon icon={["fas", "caret-left"]} />
                    </div>
                </div>
                <div
                    className={classNames(classes.panelButton, classes.panelButtonSmall)}
                    onClick={() => dispatch(navigationNextPage())}
                    data-tip={t("common.nextPage")}
                >
                    <div className={classes.panelButtonIcon}>
                        <FontAwesomeIcon icon={["fas", "caret-right"]} />
                    </div>
                </div>
                <div style={{ flex: 1 }} />
                <div
                    onClick={() => onSelectedChanged("settings")}
                    className={classNames(
                        classes.panelButton,
                        selected === "settings" && classes.panelButtonActive,
                    )}
                    data-tip={t("debug.panels.settings.title")}
                >
                    <div className={classes.panelButtonIcon}>
                        <FontAwesomeIcon icon={["far", "cog"]} />
                    </div>
                </div>
            </div>
            <ReactTooltip effect="solid" />
        </div>
    );
}
